import { Injectable, inject } from '@angular/core';

import {
  AuthenticationApiActions,
  logoutUser,
  selectCustomer
} from '@asset-solutions/customer-portal/auth/data-access';
import { CUSTOMER_PORTAL_CONFIG } from '@asset-solutions/customer-portal/config';
import { OrderService } from '@asset-solutions/customer-portal/orders/data-access';
import { NotificationService, omit } from '@asset-solutions/shared/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, concatMap, distinctUntilChanged, map, of, switchMap, takeUntil, timer } from 'rxjs';

import {
  changeOrderFilters,
  getOrdersError,
  getOrdersSuccess,
  liveMapChangeOrderFilters,
  liveMapResetOrderFilters,
  pollOrdersSuccess,
  resetCustomerIdsFilter,
  resetOrderFilters
} from './orders.actions';
import { getInitialState, selectTripStatus } from './orders.reducer';

@Injectable()
export class OrdersEffects {
  private readonly actions$ = inject(Actions);
  private readonly notificationService = inject(NotificationService);
  private readonly ordersService = inject(OrderService);
  private readonly store = inject(Store);
  private readonly pollingInterval = inject(CUSTOMER_PORTAL_CONFIG).orderSummary.pollingInterval;

  getCustomerLoads = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthenticationApiActions.authenticationSuccess, selectCustomer),
      concatLatestFrom(() => this.store.select(selectTripStatus)),
      switchMap(([, status]) =>
        this.ordersService.getOrderSummaries(status).pipe(
          map((orders) => getOrdersSuccess({ orders })),
          catchError(() => {
            this.notificationService.error('There was an issue getting your orders. Try reloading the page.');
            return of(getOrdersError());
          })
        )
      )
    );
  });

  resetCustomerIdsFilter = createEffect(() => {
    return this.actions$.pipe(
      ofType(selectCustomer),
      switchMap(() => of(resetCustomerIdsFilter()))
    );
  });

  updateCustomerLoads = createEffect(() => {
    return this.actions$.pipe(
      ofType(changeOrderFilters, liveMapChangeOrderFilters),
      map(({ filters }) => filters.tripStatus),
      distinctUntilChanged((previous, current) => previous.join('') === current.join('')),
      switchMap((status) =>
        this.ordersService.getOrderSummaries(status).pipe(
          map((orders) => getOrdersSuccess({ orders })),
          catchError(() => {
            this.notificationService.error('There was an issue getting your orders. Try reloading the page.');
            return of(getOrdersError());
          })
        )
      )
    );
  });

  resetOrderFilters = createEffect(() => {
    return this.actions$.pipe(
      ofType(resetOrderFilters, liveMapResetOrderFilters),
      map(() => changeOrderFilters({ filters: omit(getInitialState(), ['orders']) }))
    );
  });

  pollCustomerLoads = createEffect(() => {
    return this.actions$.pipe(
      ofType(getOrdersSuccess),
      concatLatestFrom(() => this.store.select(selectTripStatus)),
      switchMap(([, status]) =>
        timer(this.pollingInterval, this.pollingInterval).pipe(
          concatMap(() =>
            this.ordersService.getOrderSummaries(status).pipe(
              map((orders) => {
                this.notificationService.success('New orders were received');
                return pollOrdersSuccess({ orders });
              }),
              catchError(() => {
                this.notificationService.error(
                  'There was an issue checking for order updates. Try reloading the page.'
                );
                return of(getOrdersError());
              })
            )
          ),
          takeUntil(
            this.actions$.pipe(
              ofType(
                changeOrderFilters,
                liveMapChangeOrderFilters,
                logoutUser,
                AuthenticationApiActions.authenticationError
              )
            )
          )
        )
      )
    );
  });
}
